import type {
  RepositoryPickerRepository$data as Repository,
  RepositoryPickerTopRepositoriesQuery,
} from '@github-ui/item-picker/repository'
import {type AssigneePickerAssignee$key, AssigneeFragment} from '@github-ui/item-picker/assignee'
import {type LabelPickerLabel$key, LabelFragment} from '@github-ui/item-picker/label'
import {type ProjectPickerProject$key, ProjectPickerProjectFragment} from '@github-ui/item-picker/project'
import {clearSessionStorage, useSessionStorage} from '@github-ui/use-safe-storage'
import {Box} from '@primer/react'
import {type RefObject, Suspense, useCallback, useEffect, useImperativeHandle} from 'react'
import {type PreloadedQuery, readInlineData} from 'react-relay'

import {VALUES} from './constants'
import {CreateIssueForm, type CreateIssueCallbackProps} from './CreateIssueForm'
import {TemplatePickerButton} from './TemplatePickerButton'
import {RepositoryAndTemplatePickerDialog} from './RepositoryAndTemplatePickerDialog'

import {
  instanceOfIssueTemplateData,
  instanceOfIssueFormData,
  BLANK_ISSUE_ID,
  instanceOfBlankIssueData,
  instanceOfContactLinkData,
} from './utils/model'
import {TemplateListPane} from './TemplateListPane'
import {CreateIssueFooter, type IssueCreatePayload, storageKeys} from '.'
import {TemplateListLoading} from './TemplateList'
import {DisplayMode} from './utils/display-mode'
import type {IssueCreateUrlParams, IssueCreateValueTypes} from './utils/template-args'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {constructTemplateUrlParamsIfAppropriate, setTemplateUrlParamIfAppropriate} from './utils/urls'
import {IssueTypeFragment, type IssueTypePickerIssueType$key} from '@github-ui/item-picker/issue-type'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'

export type CreateIssueProps = {
  topReposQueryRef?: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  createIssueUrlParamsRef?: RefObject<IssueCreateUrlParams>
} & CreateIssueCallbackProps

export const CreateIssue = ({topReposQueryRef, createIssueUrlParamsRef, ...props}: CreateIssueProps) => {
  const {issueTitle, issueRepoId, issueTemplateId, issueBody} = VALUES.localStorageKeys
  const {
    repository,
    setRepository,
    template,
    setTemplate,
    preselectedRepository,
    preselectedTemplate,
    preselectedTemplates,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
  } = useIssueCreateDataContext()
  const {optionConfig} = useIssueCreateConfigContext()
  const {storageKeyPrefix} = optionConfig
  const [repositoryId, setRepositoryId] = useSessionStorage<string | undefined>(
    issueRepoId(storageKeyPrefix),
    undefined, // this is needed to be undefined for template content clean to work when changing repos
  )

  const [templateId, setTemplateId] = useSessionStorage<string | undefined>(
    issueTemplateId(storageKeyPrefix),
    undefined,
  )

  const [title, setTitle] = useSessionStorage<string>(issueTitle(storageKeyPrefix), '')
  const [body, setBody] = useSessionStorage<string>(issueBody(storageKeyPrefix), '')

  const {displayMode, setDisplayMode, setCreateMoreCreatedPath} = useIssueCreateConfigContext()

  // On first render, if we have a pre-selected repository and template, we should set these.
  useEffect(() => {
    if (preselectedRepository) {
      onRepositorySelected(preselectedRepository)

      if (preselectedTemplate) {
        // On first render - we want to take into consideration the default parameters as the default state values if no template is currently set.
        // Meaning, we take precedence of the default parameters over the template's default values, unless there are already values in sessionStorage
        // or we are changing template (this logic is handled inside the function).
        onTemplateSelected(preselectedTemplate, optionConfig.issueCreateArguments?.initialValues)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearMetadata = useCallback(() => {
    setAssignees([])
    setLabels([])
    setProjects([])
    setMilestone(null)
  }, [setAssignees, setLabels, setMilestone, setProjects])

  const clearSessionData = useCallback(() => {
    clearSessionStorage(storageKeys(storageKeyPrefix))
  }, [storageKeyPrefix])

  const constructUrlSearchParams = useCallback(
    () =>
      constructTemplateUrlParamsIfAppropriate({
        repository,
        template,
        preselectedRepository,
        title,
        body,
        assignees,
        labels,
        milestone: milestone ?? undefined,
        type: issueType ?? undefined,
        projects,
        displayMode,
      }),
    [
      repository,
      template,
      preselectedRepository,
      title,
      body,
      assignees,
      labels,
      milestone,
      issueType,
      projects,
      displayMode,
    ],
  )

  useImperativeHandle(
    createIssueUrlParamsRef,
    () => ({
      generateUrlParams: () => constructUrlSearchParams(),
    }),
    [constructUrlSearchParams],
  )

  const onRepositorySelected = useCallback(
    (selectedRepository: Repository | undefined) => {
      if (selectedRepository) {
        if (repositoryId !== selectedRepository.id) {
          // We are only clearing metadata because we want to persist title & body across repo changes incase we are selecting blank issues
          clearMetadata()
        }

        setRepository(selectedRepository)
        setRepositoryId(selectedRepository.id)
        setCreateMoreCreatedPath({
          owner: selectedRepository.owner.login,
          repo: selectedRepository.name,
          number: undefined,
        })
      }
    },
    [clearMetadata, repositoryId, setCreateMoreCreatedPath, setRepository, setRepositoryId],
  )

  const onTemplatePickerClick = useCallback(() => {
    setDisplayMode(DisplayMode.TemplatePicker)
  }, [setDisplayMode])

  const onTemplateSelected = useCallback(
    (selectedTemplate: IssueCreatePayload, enforcedInitialValues?: IssueCreateValueTypes) => {
      // Contact links will redirect to a new page, so we don't want to set the template
      if (instanceOfContactLinkData(selectedTemplate.data)) {
        return
      }

      const changedTemplates = templateId !== selectedTemplate.data?.__id
      setTemplate(selectedTemplate)
      setTemplateId(selectedTemplate.data?.__id)

      if (changedTemplates) {
        setTemplateUrlParamIfAppropriate({
          insidePortal: optionConfig.insidePortal,
          template: selectedTemplate,
          repository,
          preselectedRepository,
        })

        const previouslyWasBlankIssue = templateId === BLANK_ISSUE_ID
        clearMetadata()
        const newTitle =
          enforcedInitialValues?.title ??
          (selectedTemplate.data.title ?? '') +
            (previouslyWasBlankIssue && title && title.length > 0 ? ` ${title}` : '')
        setTitle(newTitle)

        if (instanceOfIssueTemplateData(selectedTemplate.data) || instanceOfBlankIssueData(selectedTemplate.data)) {
          const newBody =
            enforcedInitialValues?.body ??
            (!previouslyWasBlankIssue || body?.length === 0 ? '' : `${body}\n\n---\n`) + selectedTemplate.data.body
          setBody(newBody)
        } else {
          setBody('')
        }

        const templateLabels =
          enforcedInitialValues?.labels ??
          (selectedTemplate.data.labels?.edges?.flatMap(e =>
            // eslint-disable-next-line no-restricted-syntax
            e?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, e?.node)] : [],
          ) ||
            [])
        setLabels(templateLabels)

        const templateAssignees =
          enforcedInitialValues?.assignees ??
          ((selectedTemplate.data.assignees?.edges || []).flatMap(e =>
            // eslint-disable-next-line no-restricted-syntax
            e?.node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, e?.node)] : [],
          ) ||
            [])
        setAssignees(templateAssignees)

        if (enforcedInitialValues?.projects) {
          setProjects(enforcedInitialValues.projects)
        } else if (instanceOfIssueFormData(selectedTemplate.data)) {
          const templateProjects =
            selectedTemplate.data.projects?.edges?.flatMap(e =>
              // eslint-disable-next-line no-restricted-syntax
              e?.node ? [readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, e?.node)] : [],
            ) || []
          setProjects(templateProjects)
        }

        if (enforcedInitialValues?.milestone) {
          setMilestone(enforcedInitialValues.milestone)
        }

        let templateType = enforcedInitialValues?.type || null
        if (
          !templateType &&
          (instanceOfIssueFormData(selectedTemplate.data) || instanceOfIssueTemplateData(selectedTemplate.data))
        ) {
          // eslint-disable-next-line no-restricted-syntax
          templateType = readInlineData<IssueTypePickerIssueType$key>(IssueTypeFragment, selectedTemplate.data.type)
        }
        setIssueType(templateType)
      }

      setDisplayMode(DisplayMode.IssueCreation)
    },
    [
      templateId,
      setTemplate,
      setTemplateId,
      setDisplayMode,
      optionConfig.insidePortal,
      repository,
      preselectedRepository,
      clearMetadata,
      title,
      setTitle,
      setLabels,
      setAssignees,
      setIssueType,
      body,
      setBody,
      setProjects,
      setMilestone,
    ],
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2}} tabIndex={-1}>
      {displayMode === DisplayMode.IssueCreation && (
        <>
          {topReposQueryRef && (
            <Box sx={{width: 'content'}} data-testid="template-picker-button">
              <TemplatePickerButton repository={repository} template={template} onClick={onTemplatePickerClick} />
            </Box>
          )}
          {repository && (
            <>
              <CreateIssueForm
                {...props}
                selectedTemplate={template}
                repository={repository}
                title={title}
                setTitle={setTitle}
                body={body}
                setBody={setBody}
                clearOnCreate={clearSessionData}
                focusTitleInput={!topReposQueryRef}
              />
              {/* The dialog renders the footer separately */}
              {!optionConfig.insidePortal && <CreateIssueFooter sx={{mt: 2}} onClose={props.onCancel} />}
            </>
          )}
        </>
      )}
      {optionConfig.showRepositoryPicker && displayMode === DisplayMode.TemplatePicker && topReposQueryRef && (
        <RepositoryAndTemplatePickerDialog
          repository={repository}
          setRepository={onRepositorySelected}
          topReposQueryRef={topReposQueryRef}
          organization={optionConfig.scopedOrganization}
          onTemplateSelected={onTemplateSelected}
          variant="large"
        />
      )}
      {!optionConfig.showRepositoryPicker && displayMode === DisplayMode.TemplatePicker && repository && (
        <Suspense fallback={<TemplateListLoading />}>
          {repository && (
            <Box data-hpc>
              <TemplateListPane
                variant="full"
                repository={repository}
                templates={preselectedTemplates}
                onTemplateSelected={onTemplateSelected}
              />
            </Box>
          )}
        </Suspense>
      )}
    </Box>
  )
}

try{ CreateIssue.displayName ||= 'CreateIssue' } catch {}